/**
 * Builds the one-line record of what a setup revision did. Pure: it formats
 * text and writes nowhere.
 */

import type { NavigationDestinationKind, NavigationSource } from './navigationIntent';
import type { ProjectScopeResetReason } from './projectScopeReconciliation';
import type { SetupRevisionFailureKind } from './revisionFailure';
import type { SetupRevisionStatus } from './revisionState';

export type SetupRevisionLogEvent =
  | 'revisionOpened'
  | 'phaseEntered'
  | 'revisionSettled'
  | 'revisionFailed'
  | 'projectScopeReset';

export interface SetupRevisionLogFields {
  revisionId: number;
  phase: SetupRevisionStatus;
  projectKey?: string | null;
  source?: NavigationSource | null;
  destination?: NavigationDestinationKind | null;
  planId?: string | null;
  failure?: SetupRevisionFailureKind | null;
  /** Why hydrated project-scoped state was discarded rather than adopted. */
  scopeReset?: ProjectScopeResetReason | null;
}

type LogValue = string | number | null | undefined;

/**
 * The whole vocabulary of the log line. Output is built from this list alone,
 * never by walking the caller's object, so a profile payload or any other
 * field that is not named here can never reach the log.
 */
const LoggedFields: ReadonlyArray<readonly [string, (fields: SetupRevisionLogFields) => LogValue]> = [
  ['revision', (fields) => fields.revisionId],
  ['phase', (fields) => fields.phase],
  ['project', (fields) => fields.projectKey],
  ['source', (fields) => fields.source],
  ['destination', (fields) => fields.destination],
  ['plan', (fields) => fields.planId],
  ['failure', (fields) => fields.failure],
  ['scopeReset', (fields) => fields.scopeReset],
];

/** Keeps one revision on one line, whatever whitespace a key happens to carry. */
const oneLine = (value: string | number): string => String(value).replace(/\s+/g, ' ').trim();

export const formatSetupRevisionLog = (
  event: SetupRevisionLogEvent,
  fields: SetupRevisionLogFields
): string => {
  const parts: string[] = [];

  LoggedFields.forEach(([name, read]) => {
    const raw = read(fields);
    if (raw === null || raw === undefined) {
      return;
    }

    const value = oneLine(raw);
    if (!value) {
      return;
    }

    parts.push(`${name}=${value}`);
  });

  return ['setup-revision', event, ...parts].join(' ');
};
